/**
 * The reading pane, cut into the pieces it actually renders.
 *
 * The speech chunker (findSentenceBoundaries) reports where each spoken chunk
 * starts and ends in the display text, and nothing else. Everything between two
 * chunks - the blank lines that make a paragraph, the page break token, the
 * image markers the chunker steps over - is text the reader must still see, and
 * it exists only as the gaps between those character ranges.
 *
 * The screen used to walk that itself, rendering the gap *before* each chunk. It
 * therefore never rendered the gap after the last one, so everything past the
 * final chunk's endCharIndex was dropped. On a PDF that is the closing
 * `\n<STRONA>\n`, which is the last page separator: visible while the document
 * sat idle, gone the moment the reader pressed play.
 *
 * The walk lives here rather than in the screen because the screen imports PDF.js
 * and cannot be mounted in these tests, and the tail is exactly the kind of thing
 * that gets dropped again. It is a pure function over the text and the chunk
 * list, and the screen maps its output to elements.
 *
 * DYS-045 then made this the *only* way the reading pane is rendered, idle
 * document included, so what it produces has to match what the unchunked
 * renderer produced character for character. Three things were not matching, and
 * each is handled here rather than in the screen so that it can be tested:
 *
 * 1. Page content was trimmed by the old renderer (`pageContent.trim()`), so the
 *    newlines around `<STRONA>` did not also render as blank lines above and
 *    below the separator.
 * 2. The "each sentence on its own line" setting rewrote `. ` into `.\n` across
 *    the whole document. Between two chunks that space is a gap run, and nothing
 *    was rewriting it - so switching the setting on did nothing during playback.
 * 3. An image block is `[OBRAZ] ... [/OBRAZ]`, which the old renderer matched as
 *    a whole and turned into an icon and its text. The chunker splits it - the
 *    markers are their own tokens and the text inside is an ordinary sentence -
 *    so the two markers ended up in different runs, matched nothing, and were
 *    printed raw. Raw markup in front of a dyslexic reader is the thing this app
 *    exists to avoid; see the editable-text note in text-display-screen.tsx. The
 *    block is therefore reassembled here as a run that nests the runs inside it.
 */

import { PAGE_BREAK_TOKEN } from './constants'
import type { SentenceBoundary } from './text-util'

const IMAGE_BLOCK_OPEN = '[OBRAZ]'
const IMAGE_BLOCK_CLOSE = '[/OBRAZ]'
const IMAGE_BLOCK_PATTERN = /\[OBRAZ\][\s\S]*?\[\/OBRAZ\]/g

/** Sentence terminators, and the marks that may sit between one and the space. */
const SENTENCE_TERMINATORS = '.!?'
const CLOSING_PUNCTUATION = '"\'”’)]»'

/** A gap made of nothing but whitespace: what the sentence setting replaces. */
const WHITESPACE_ONLY = /^\s+$/

/**
 * One piece of the document, in reading order.
 *
 * - `text` is a run between chunks: whitespace, and any marker the chunker skips.
 * - `pageBreak` is where a PAGE_BREAK_TOKEN stood. `pageNumber` counts the breaks
 *   seen so far, starting at 1, which is what the separator component renders as
 *   "Strona 2" - the page the break opens, not the one it closes.
 * - `chunk` points back into the chunk list by index, so the caller keeps its own
 *   decisions about highlighting and word spans.
 * - `imageBlock` holds the runs that stood between `[OBRAZ]` and `[/OBRAZ]`, with
 *   the markers themselves removed. The chunks inside it are still chunks, so the
 *   highlight moves through an image block like anywhere else.
 */
export type ChunkedTextRun =
  | { kind: 'text'; text: string }
  | { kind: 'pageBreak'; pageNumber: number }
  | { kind: 'chunk'; index: number }
  | { kind: 'imageBlock'; runs: ChunkedTextRun[] }

/** The part of a chunk this needs: where it sits in the display text. */
type ChunkRange = Pick<SentenceBoundary, 'startCharIndex' | 'endCharIndex'>

export interface ChunkedTextRunOptions {
  /**
   * The reader's "each sentence on its own line" setting. A chunk boundary that
   * closes a sentence then renders as a line end rather than as the space that
   * is in the source.
   */
  sentenceOnNewLine?: boolean
}

/** A run, plus where it came from - needed only while the runs are assembled. */
interface LocatedRun {
  run: ChunkedTextRun
  start: number
  end: number
}

interface ImageBlockSpan {
  start: number
  innerStart: number
  innerEnd: number
  end: number
}

/**
 * Removes the image markers from a string without touching anything else.
 *
 * A chunk can carry one: the chunker's sentence range starts at the `[` of
 * `[OBRAZ]`, so the opening marker is part of the chunk's own text rather than of
 * the gap before it. The space that followed the marker goes with it, so the text
 * inside the block starts where it did before.
 */
export function stripImageBlockMarkers(text: string): string {
  return text.replace(/\[OBRAZ\][^\S\n\r]*/g, '').replace(/[^\S\n\r]*\[\/OBRAZ\]/g, '')
}

function findImageBlockSpans(text: string): ImageBlockSpan[] {
  const spans: ImageBlockSpan[] = []
  const pattern = new RegExp(IMAGE_BLOCK_PATTERN.source, 'g')
  let match: RegExpExecArray | null

  while ((match = pattern.exec(text)) !== null) {
    const start = match.index
    const end = start + match[0].length
    spans.push({
      start,
      innerStart: start + IMAGE_BLOCK_OPEN.length,
      innerEnd: end - IMAGE_BLOCK_CLOSE.length,
      end,
    })
  }

  return spans
}

/** True when the text just before `position` ends a sentence. */
function endsSentenceBefore(text: string, position: number): boolean {
  let index = position - 1
  while (index >= 0 && CLOSING_PUNCTUATION.includes(text[index])) index -= 1
  return index >= 0 && SENTENCE_TERMINATORS.includes(text[index])
}

/**
 * Splits `text` into the runs that render it, given the chunks found in it.
 *
 * The chunks are assumed to be in order and not to overlap, which is what
 * findSentenceBoundaries produces. An empty chunk list yields the whole text as a
 * single run sequence - the caller may still prefer its own unchunked path there.
 */
export function buildChunkedTextRuns(
  text: string,
  chunks: readonly ChunkRange[],
  options: ChunkedTextRunOptions = {}
): ChunkedTextRun[] {
  const spans = findImageBlockSpans(text)
  const located = collectRuns(text, chunks, spans)

  if (options.sentenceOnNewLine) {
    applySentenceLineBreaks(located, text)
  }

  trimPageEdges(located)

  return nestImageBlocks(
    located.filter((item) => item.run.kind !== 'text' || item.run.text.length > 0),
    spans
  )
}

/** The flat walk: gap, chunk, gap, chunk, ... and finally the tail. */
function collectRuns(
  text: string,
  chunks: readonly ChunkRange[],
  spans: ImageBlockSpan[]
): LocatedRun[] {
  const located: LocatedRun[] = []
  let pageBreakCount = 0

  // Where a text run may not continue across: the two image markers are cut out
  // of the gaps entirely, and the block's edges keep a run from straddling them.
  const cuts = new Set<number>()
  for (const span of spans) {
    cuts.add(span.start)
    cuts.add(span.innerStart)
    cuts.add(span.innerEnd)
    cuts.add(span.end)
  }
  const isMarkerRange = (start: number, end: number) =>
    spans.some(
      (span) =>
        (start === span.start && end === span.innerStart) ||
        (start === span.innerEnd && end === span.end)
    )

  const pushText = (from: number, to: number) => {
    if (to <= from) return

    const points = [from, ...[...cuts].filter((cut) => cut > from && cut < to), to].sort(
      (a, b) => a - b
    )

    for (let index = 0; index < points.length - 1; index += 1) {
      const start = points[index]
      const end = points[index + 1]
      if (isMarkerRange(start, end)) continue
      located.push({ run: { kind: 'text', text: text.slice(start, end) }, start, end })
    }
  }

  const pushGap = (from: number, to: number) => {
    if (to <= from) return

    let cursor = from
    let tokenIndex = text.indexOf(PAGE_BREAK_TOKEN, cursor)

    while (tokenIndex !== -1 && tokenIndex < to) {
      pushText(cursor, tokenIndex)
      pageBreakCount += 1
      located.push({
        run: { kind: 'pageBreak', pageNumber: pageBreakCount },
        start: tokenIndex,
        end: tokenIndex + PAGE_BREAK_TOKEN.length,
      })
      cursor = tokenIndex + PAGE_BREAK_TOKEN.length
      tokenIndex = text.indexOf(PAGE_BREAK_TOKEN, cursor)
    }

    pushText(cursor, to)
  }

  let lastEndIndex = 0

  chunks.forEach((chunk, index) => {
    pushGap(lastEndIndex, chunk.startCharIndex)
    located.push({
      run: { kind: 'chunk', index },
      start: chunk.startCharIndex,
      end: chunk.endCharIndex,
    })
    lastEndIndex = Math.max(lastEndIndex, chunk.endCharIndex)
  })

  // The tail: everything after the last chunk. The whole point of this helper.
  pushGap(lastEndIndex, text.length)

  return located
}

/**
 * Turns the gap between two sentences into a line end.
 *
 * The whole gap, not just a space: the rewrite this reproduces was
 * `/\.\s+/ -> '.\n'` over the whole document, which swallowed whatever
 * whitespace followed the period - so a sentence that ended a paragraph lost the
 * blank line after it. That is what the reader with this setting on has always
 * seen, and matching it exactly is the point here; a gap that reads better is a
 * separate argument to have with the setting itself.
 *
 * A gap carrying anything but whitespace is not a sentence boundary and is left
 * alone. The chunker splits sentences through the same abbreviation check that
 * rewrite used, so "np. tak" is one chunk and never gets a break inside it.
 */
function applySentenceLineBreaks(located: LocatedRun[], text: string) {
  for (const item of located) {
    if (item.run.kind !== 'text') continue
    if (!WHITESPACE_ONLY.test(item.run.text)) continue
    if (!endsSentenceBefore(text, item.start)) continue

    item.run = { kind: 'text', text: '\n' }
  }
}

/**
 * Trims the whitespace at each end of a page, which is what the old renderer did
 * by trimming the page's whole text before formatting it.
 *
 * Only when the document has page breaks at all: with none, the old renderer
 * passed the text through untouched, and a document that opens with a blank line
 * has to keep it.
 */
function trimPageEdges(located: LocatedRun[]) {
  if (!located.some((item) => item.run.kind === 'pageBreak')) return

  located.forEach((item, index) => {
    if (item.run.kind !== 'text') return

    const opensPage = index === 0 || located[index - 1].run.kind === 'pageBreak'
    const closesPage =
      index === located.length - 1 || located[index + 1].run.kind === 'pageBreak'

    let value = item.run.text
    if (opensPage) value = value.replace(/^\s+/, '')
    if (closesPage) value = value.replace(/\s+$/, '')

    item.run = { kind: 'text', text: value }
  })
}

/** Collects the runs inside each `[OBRAZ] ... [/OBRAZ]` span into one run. */
function nestImageBlocks(located: LocatedRun[], spans: ImageBlockSpan[]): ChunkedTextRun[] {
  if (spans.length === 0) return located.map((item) => item.run)

  const runs: ChunkedTextRun[] = []
  let index = 0

  for (const span of spans) {
    while (index < located.length && located[index].start < span.start) {
      runs.push(located[index].run)
      index += 1
    }

    const nested: ChunkedTextRun[] = []
    while (
      index < located.length &&
      located[index].start >= span.start &&
      located[index].end <= span.end
    ) {
      nested.push(located[index].run)
      index += 1
    }

    if (nested.length > 0) {
      runs.push({ kind: 'imageBlock', runs: trimBlockEdges(nested) })
    }
  }

  while (index < located.length) {
    runs.push(located[index].run)
    index += 1
  }

  return runs
}

/** The old renderer trimmed the text inside an image block; so does this. */
function trimBlockEdges(runs: ChunkedTextRun[]): ChunkedTextRun[] {
  const trimmed = runs.map((run, index) => {
    if (run.kind !== 'text') return run

    let value = run.text
    if (index === 0) value = value.replace(/^\s+/, '')
    if (index === runs.length - 1) value = value.replace(/\s+$/, '')

    return { kind: 'text', text: value } as ChunkedTextRun
  })

  return trimmed.filter((run) => run.kind !== 'text' || run.text.length > 0)
}
